import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api } from '../api/client';
import type { BillingSummary, BillingUserSummary, SettlementDto } from '../api/types';
import { RoleBadge } from '../components/status';
import { Card, ConfirmDialog, Empty, ErrorAlert, Field, Spinner } from '../components/ui';
import { formatDateTime, formatNumber } from '../lib/format';

function formatTotals(totals: Record<string, number>): string {
  const entries = Object.entries(totals);
  if (entries.length === 0) return '0';
  return entries.map(([currency, amount]) => `${amount.toFixed(2)} ${currency}`).join(' + ');
}

export function BillingPage() {
  const queryClient = useQueryClient();
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [settling, setSettling] = useState<BillingUserSummary | null>(null);
  const [settled, setSettled] = useState<SettlementDto | null>(null);
  const [pageError, setPageError] = useState<unknown>(null);

  const summary = useQuery({
    queryKey: ['billing', { from, to }],
    queryFn: () =>
      api.get<BillingSummary>('/billing/summary', {
        from: from || undefined,
        to: to || undefined,
      }),
  });

  const settle = useMutation({
    mutationFn: (row: BillingUserSummary) =>
      api.post<SettlementDto>('/billing/settle', { userEmail: row.userEmail }),
    onSuccess: (result) => {
      setSettling(null);
      setSettled(result);
      void queryClient.invalidateQueries({ queryKey: ['billing'] });
    },
    onError: (error) => {
      setSettling(null);
      setPageError(error);
    },
  });

  const data = summary.data;

  return (
    <main className="page">
      <div className="page-header">
        <div>
          <h1>Facturación</h1>
          <p>
            Deuda <strong>pendiente</strong> de cada usuario del panel. El cargo se devenga al
            crear (o renovar) y no desaparece aunque se borre lo creado. «Marcar pagado» hace
            el corte de caja: el contador vuelve a cero y el historial queda en los pagos.
          </p>
        </div>
      </div>

      <ErrorAlert error={summary.error} />
      <ErrorAlert error={pageError} onDismiss={() => setPageError(null)} />

      {settled && (
        <div className="alert ok">
          <strong>
            Pago de {settled.userEmail} registrado: {formatTotals(settled.totals)} (
            {formatNumber(settled.chargesCount)} creaciones).
          </strong>{' '}
          Su contador vuelve a cero.
          <button type="button" className="ghost small" style={{ marginLeft: 8 }} onClick={() => setSettled(null)}>
            Cerrar
          </button>
        </div>
      )}

      <div className="toolbar">
        <Field label="Desde">
          <input type="date" value={from} onChange={(event) => setFrom(event.target.value)} />
        </Field>
        <Field label="Hasta">
          <input type="date" value={to} onChange={(event) => setTo(event.target.value)} />
        </Field>
        {(from || to) && (
          <button
            type="button"
            className="ghost small"
            onClick={() => {
              setFrom('');
              setTo('');
            }}
          >
            Quitar filtro
          </button>
        )}
        {summary.isFetching && <Spinner label="" />}
      </div>

      <Card
        title="Deuda por usuario"
        actions={
          data && Object.keys(data.grandTotals).length > 0 ? (
            <strong>Total: {formatTotals(data.grandTotals)}</strong>
          ) : undefined
        }
      >
        {summary.isLoading ? (
          <div className="card-body">
            <Spinner />
          </div>
        ) : data && data.users.length > 0 ? (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Usuario del panel</th>
                  <th className="num">HotSpot</th>
                  <th className="num">Vouchers</th>
                  <th className="num">PPPoE</th>
                  <th className="num">Renovaciones</th>
                  <th className="num">Debe</th>
                  <th>Último cargo</th>
                  <th className="actions" />
                </tr>
              </thead>
              <tbody>
                {data.users.map((row) => (
                  <tr key={row.userEmail}>
                    <td>
                      <strong>{row.userName ?? row.userEmail}</strong>
                      <div className="faint" style={{ fontSize: 12 }}>
                        {row.userEmail}
                        {row.isActive === false && ' · cuenta desactivada'}
                        {row.userId === null && ' · cuenta eliminada'}
                      </div>
                      {row.role && <RoleBadge role={row.role} />}
                    </td>
                    <td className="num">{formatNumber(row.counts.hotspotUsers)}</td>
                    <td className="num">{formatNumber(row.counts.vouchers)}</td>
                    <td className="num">{formatNumber(row.counts.pppoeClients)}</td>
                    <td className="num">{formatNumber(row.counts.renewals)}</td>
                    <td className="num">
                      <strong>{formatTotals(row.totals)}</strong>
                    </td>
                    <td className="faint">{formatDateTime(row.lastChargeAt)}</td>
                    <td className="actions">
                      <button
                        type="button"
                        className="small primary"
                        disabled={settle.isPending}
                        onClick={() => setSettling(row)}
                      >
                        Marcar pagado
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <Empty>
            Sin cargos en este período. Los cargos se generan al crear usuarios, vouchers o
            clientes a partir de ahora; lo creado antes de activar la facturación no devengó
            cargo.
          </Empty>
        )}
      </Card>

      <p className="faint" style={{ fontSize: 12 }}>
        El importe de cada cargo es el precio que tenía el plan en ese momento. Cambiar el
        precio del plan después no altera los cargos ya devengados. Un plan con precio 0
        genera cargos de 0: sube el precio del plan para que la deuda refleje tu tarifa.
      </p>

      {settling && (
        <ConfirmDialog
          title={`Marcar pagado a ${settling.userName ?? settling.userEmail}`}
          busy={settle.isPending}
          confirmLabel={`Confirmar pago de ${formatTotals(settling.totals)}`}
          message={
            <p>
              Se da por cobrada TODA la deuda pendiente (
              {formatNumber(
                settling.counts.hotspotUsers +
                  settling.counts.vouchers +
                  settling.counts.pppoeClients +
                  settling.counts.renewals,
              )}{' '}
              creaciones). Su contador vuelve a cero y este corte queda guardado en su
              historial de pagos y en la auditoría. No se puede deshacer.
            </p>
          }
          onCancel={() => setSettling(null)}
          onConfirm={() => settle.mutate(settling)}
        />
      )}
    </main>
  );
}
